import React, { useState, useEffect, useRef } from 'react';
import {OverTimeButton, OverTimeDropdown} from "../../StyledComponents";
import OverTimeChart from '../../components/OverTime';
import { getString } from "../../utils/translations";
import { findIndex, get, cloneDeep } from 'lodash';
import {BLENDSHAPES_NAMES, OVER_TIME_BUTTONS} from "../../constants";
import { FACE_CERTAINTY_FRAMES } from "../../api/remoteConfig";
import IdButtons from "../../components/IdButtons";
import colors from "../../styles/colors";
import CSVModuleBuilder from "../../components/CSVModuleBuilder";
import fonts from "../../styles/fonts";

const OVERTIME_CHART_HEIGHT = 250;

const SoloOverTimeChart = ({
                               overtime,
                               faceIds,
                               faceIdCount,
                               selectedIds,
                               showBrush,
                               onBrushChange,
                               toggleFaceLandmarks,
                               resetOverTime,
                               selectId,
                               showFaceLandmarks,
                               isJP,
                               uploadModules,
                               uploadModulesHandler,
                               customModules,
    showAllBlendshapes = false
                           }) => {
    const [selectedButtons, setSelectedButtons] = useState([OVER_TIME_BUTTONS.STRESS]);
    const [chartShowAllTime, setChartShowAllTime] = useState(false);
    const isLabEnv = process.env.REACT_APP_ENV === "lab";

    const handleButtonSelect = (value) => {
        setSelectedButtons([value]);
    };

    const handleDropdownChange = (value) => {
        setSelectedButtons([value]);
    };

    const renderDropdown = (label, options, placeholder) => (
        <div style={{ marginBottom: 10 }}>
            <div
                style={{
                    width: 98,
                    height: 23,
                    border: "none",
                    backgroundColor: colors.gray,
                    position: 'relative',
                }}
            >
                <OverTimeDropdown
                    onChange={(e) => handleDropdownChange(e.target.value)}
                    value={""}
                    style={{
                        width: '100%',
                        fontFamily: fonts.OpenSans,
                        fontSize: 13,
                    }}
                >
                    <option style={{
                        fontFamily: fonts.OpenSans,
                        fontSize: 13,
                    }} value="" disabled hidden>
                        {placeholder}
                    </option>
                    {options.map((key) => (
                        <option key={key} value={key}>
                            {getString(key)}
                        </option>
                    ))}
                </OverTimeDropdown>
            </div>
        </div>
    );

    const renderSelectedValue = (value) => (
        <div
            style={{
                width: 98,
                height: 23,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: colors.blue,
                color: 'white',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: 13,
                fontFamily: fonts.OpenSans,
            }}
        >
            {getString(value)}
        </div>
    );

    const renderButtonColumn = (buttons) => (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 5,
            width: 110, 
            marginRight: 5,
            marginLeft: 5 
        }}>
            {buttons.map((button) => (
                <OverTimeButton
                    key={button}
                    style={{
                        backgroundColor: selectedButtons.includes(button) ? colors.blue : "#909090",
                        //color: selectedButtons.includes(button) ? colors.white : colors.black,
                        borderRadius: 0,
                        width: '100%',
                        fontSize: 11,
                        padding: '4px 2px',
                        textAlign: 'center',
                        borderRadius: 10
                    }}
                    onClick={() => handleButtonSelect(button)}
                >
                    {getString(button)}
                </OverTimeButton>
            ))}
        </div>
    );

    const renderIdsButtons = (ids) => {
        const filteredIds = ids.filter((key) => faceIdCount[key] >= FACE_CERTAINTY_FRAMES);

        return (
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ gap: 15, display: 'flex', flexDirection: 'row' }}>
                    <OverTimeButton
                        style={{ backgroundColor: colors.black, color: colors.white, borderRadius: 0 }}
                        onClick={resetOverTime}
                    >
                        {getString("reset_data")}
                    </OverTimeButton>
                </div>
                <div style={{ marginLeft: 15, marginRight: 15, flex: 1 }}>
                    <IdButtons ids={filteredIds} selectedIds={selectedIds} selectId={selectId} />
                </div>
                <div style={{ gap: 15, display: 'flex', flexDirection: 'row' }}>
                    <OverTimeButton
                        style={{ backgroundColor: colors.black, color: colors.white, borderRadius: 0, width: 150 }}
                        onClick={() => setChartShowAllTime(!chartShowAllTime)}
                    >
                        {chartShowAllTime ? getString("chart_show_all_time") : getString("chart_show_30")}
                    </OverTimeButton>
                    <OverTimeButton
                        style={{ backgroundColor: colors.black, color: colors.white, borderRadius: 0, width: 150 }}
                        onClick={toggleFaceLandmarks}
                    >
                        {showFaceLandmarks ? getString("hide_landmarks") : getString("show_landmarks")}
                    </OverTimeButton>
                </div>
            </div>
        );
    };

    let modified = cloneDeep(overtime);
    if (!chartShowAllTime && modified.length > 1) {
        const totalTimeSpent = modified[modified.length - 1].timeSpent;
        modified = modified.filter(item => item.timeSpent >= totalTimeSpent - 30 * 1000);
    }

    let domain = [0, 100];
    if (selectedButtons.length === 1) {
        const button = selectedButtons[0];
        if (button === OVER_TIME_BUTTONS.YAW_ANGLE || button === OVER_TIME_BUTTONS.yawFallback) {
            domain = [-90, 90];
        } else if (button === OVER_TIME_BUTTONS.PITCH_ANGLE || button === OVER_TIME_BUTTONS.pitchFallback) {
            domain = [-90, 90];
        } else if (button === OVER_TIME_BUTTONS.FROWN) {
            domain = [-100, 100];
        } else if (button === OVER_TIME_BUTTONS.FROWN_SCORE) {
            domain = [0, 100];
        }
    }

    let colAButtons = [OVER_TIME_BUTTONS.MOOD, OVER_TIME_BUTTONS.ENERGY, OVER_TIME_BUTTONS.STRESS, OVER_TIME_BUTTONS.ENGAGEMENT];
    let colBButtons = [
        OVER_TIME_BUTTONS.YAWN_RATIO,
        OVER_TIME_BUTTONS.EYES,
        OVER_TIME_BUTTONS.YAW_ANGLE,
        OVER_TIME_BUTTONS.PITCH_ANGLE,
        OVER_TIME_BUTTONS.FROWN,
        OVER_TIME_BUTTONS.BLINKS_PER_MINUTE,
        OVER_TIME_BUTTONS.FILTER_REASON
    ];
    let colCButtons = (customModules && customModules.length > 0) ? customModules.map((module) => module.moduleName) : [];
    let colDButtons = showAllBlendshapes ? BLENDSHAPES_NAMES : [];

    // Split buttons for left and right columns when not in lab env
    let leftColumnButtons = [...colAButtons];
    let rightColumnButtons = [...colBButtons];
    
    if (colCButtons.length > 0) {
        // Add custom modules to whichever column is shorter
        if (leftColumnButtons.length <= rightColumnButtons.length) {
            leftColumnButtons = [...leftColumnButtons, ...colCButtons];
        } else {
            rightColumnButtons = [...rightColumnButtons, ...colCButtons];
        }
    }

    return (
        <div style={{ height: OVERTIME_CHART_HEIGHT, position: 'absolute', left: 0, right: 0, bottom: 0 }}>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 12
                }}
            >
                {renderIdsButtons(faceIds)}
                <div style={{ display: 'flex', flexDirection: 'row', marginTop: 10, height: "100%" }}>
                    {isLabEnv ? (
                        // Lab environment - show dropdowns on left side
                        <div style={{ width: 93, marginRight: 15 }}>
                            {renderSelectedValue(selectedButtons[0])}
                            {renderDropdown(getString("Emotions"), colAButtons, getString("Emotions"))}
                            {renderDropdown(getString("Landmarks"), colBButtons, getString("Landmarks"))}
                            {colCButtons.length > 0 && renderDropdown(getString('Indexes'), colCButtons, getString('Indexes'))}
                            {colDButtons.length > 0 && renderDropdown(getString('Blendshapes'), colDButtons, getString('Blendshapes'))}
                        </div>
                    ) : (
                        // Non-lab environment - show buttons on left side
                        renderButtonColumn(leftColumnButtons)
                    )}
                    
                    <div style={{ flex: 1 }}>
                        <OverTimeChart
                            x={{ key: 'index' }}
                            y={{ key: 'value' }}
                            data={modified}
                            selectedEmotions={selectedButtons}
                            tooltipLabelFormatter={(payload) => get(payload, 'metadata.title', payload)}
                            domain={domain}
                            selectedIds={selectedIds}
                            showBrush={showBrush}
                            onBrushChange={onBrushChange}
                        />
                    </div>
                    
                    {!isLabEnv && (
                        // Non-lab environment - show buttons on right side
                        renderButtonColumn(rightColumnButtons)
                    )}
                    
                </div>
            </div>
        </div>
    );
};

export default SoloOverTimeChart;
